/**
 * workflowGenerator.js — Dynamic workflow generation based on outcome + selected apps
 *
 * Core principle: Every selected app MUST appear in the generated workflow.
 * The outcome determines the structure; apps determine the specific steps.
 */

/* ─────────────────────────────────────────────── */
/*  APP ROLE DEFINITIONS                            */
/* ─────────────────────────────────────────────── */

const APP_ROLES = {
  // Email
  'Gmail': {
    capabilities: ['trigger', 'output'],
    trigger: 'New email arrives in Gmail',
    process: null,
    output: 'Send reply via Gmail',
    icon: 'email',
    category: 'communication',
  },
  'Outlook': {
    capabilities: ['trigger', 'output'],
    trigger: 'New email arrives in Outlook',
    process: null,
    output: 'Send reply via Outlook',
    icon: 'email',
    category: 'communication',
  },

  // Calendar
  'Google Calendar': {
    capabilities: ['process', 'trigger'],
    trigger: 'New event in Google Calendar',
    process: 'Check Google Calendar for availability',
    output: null,
    icon: 'calendar',
    category: 'scheduling',
  },
  'Calendly': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Check Calendly for open slots',
    output: 'Create booking in Calendly',
    icon: 'calendar',
    category: 'scheduling',
  },

  // Data & Storage
  'Google Sheets': {
    capabilities: ['process'],
    trigger: null,
    process: 'Save details to Google Sheets',
    output: null,
    icon: 'spreadsheet',
    category: 'data',
  },
  'Google Drive': {
    capabilities: ['process'],
    trigger: null,
    process: 'Store document in Google Drive',
    output: null,
    icon: 'storage',
    category: 'data',
  },

  // Notifications
  'Slack': {
    capabilities: ['output'],
    trigger: null,
    process: null,
    output: 'Send notification to Slack',
    icon: 'chat',
    category: 'notification',
  },
  'WhatsApp': {
    capabilities: ['trigger', 'output'],
    trigger: 'New WhatsApp message received',
    process: null,
    output: 'Send WhatsApp message to customer',
    icon: 'chat',
    category: 'communication',
  },

  // CRM
  'HubSpot': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Create or update contact in HubSpot',
    output: null,
    icon: 'crm',
    category: 'crm',
  },
  'Pipedrive': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create deal in Pipedrive',
    output: null,
    icon: 'crm',
    category: 'crm',
  },
  'Salesforce': {
    capabilities: ['process'],
    trigger: null,
    process: 'Update lead in Salesforce',
    output: null,
    icon: 'crm',
    category: 'crm',
  },

  // Accounting
  'Xero': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Create invoice in Xero',
    output: 'Send invoice via Xero',
    icon: 'finance',
    category: 'accounting',
  },
  'QuickBooks': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Create invoice in QuickBooks',
    output: 'Send invoice via QuickBooks',
    icon: 'finance',
    category: 'accounting',
  },
  'MYOB': {
    capabilities: ['process'],
    trigger: null,
    process: 'Log transaction in MYOB',
    output: null,
    icon: 'finance',
    category: 'accounting',
  },

  // Payment
  'Stripe': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Generate payment link via Stripe',
    output: 'Send payment request via Stripe',
    icon: 'payment',
    category: 'payment',
  },

  // Marketing
  'Mailchimp': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Add contact to Mailchimp list',
    output: 'Send follow-up email via Mailchimp',
    icon: 'marketing',
    category: 'marketing',
  },

  // Job Management
  'Jobber': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create job in Jobber',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'ServiceM8': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create job in ServiceM8',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'Housecall Pro': {
    capabilities: ['process'],
    trigger: null,
    process: 'Schedule job in Housecall Pro',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'Tradify': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create quote in Tradify',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'Fergus': {
    capabilities: ['process'],
    trigger: null,
    process: 'Log job in Fergus',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'ServiceTitan': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create job in ServiceTitan',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'simPRO': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create quote in simPRO',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'FieldPulse': {
    capabilities: ['process'],
    trigger: null,
    process: 'Schedule in FieldPulse',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'ZenMaid': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create booking in ZenMaid',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'AccuLynx': {
    capabilities: ['process'],
    trigger: null,
    process: 'Log project in AccuLynx',
    output: null,
    icon: 'job',
    category: 'job_management',
  },
  'JobNimbus': {
    capabilities: ['process'],
    trigger: null,
    process: 'Create contact in JobNimbus',
    output: null,
    icon: 'job',
    category: 'job_management',
  },

  // Reviews
  'Google Business Profile': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Check Google reviews',
    output: 'Request review on Google',
    icon: 'review',
    category: 'reviews',
  },
  'Trustpilot': {
    capabilities: ['output'],
    trigger: null,
    process: null,
    output: 'Request review on Trustpilot',
    icon: 'review',
    category: 'reviews',
  },

  // Social
  'Facebook': {
    capabilities: ['trigger', 'output'],
    trigger: 'New Facebook message received',
    process: null,
    output: 'Reply on Facebook',
    icon: 'social',
    category: 'social',
  },
  'Instagram': {
    capabilities: ['trigger', 'output'],
    trigger: 'New Instagram DM received',
    process: null,
    output: 'Reply on Instagram',
    icon: 'social',
    category: 'social',
  },

  // Meetings
  'Zoom': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Schedule Zoom meeting',
    output: 'Send Zoom meeting invite',
    icon: 'video',
    category: 'meetings',
  },

  // Calendar
  'Google Calendar': {
    capabilities: ['process', 'output'],
    trigger: null,
    process: 'Create calendar event',
    output: 'Write confirmed booking to calendar',
    icon: 'calendar',
    category: 'scheduling',
  },

  // Automation
  'Zapier': {
    capabilities: ['process'],
    trigger: null,
    process: 'Trigger Zapier automation',
    output: null,
    icon: 'automation',
    category: 'automation',
  },
};

/* ─────────────────────────────────────────────── */
/*  OUTCOME-SPECIFIC PROCESSING STEPS               */
/* ─────────────────────────────────────────────── */

const OUTCOME_PROCESS_STEPS = {
  'never-miss-lead': [
    'Extract customer name, contact, and request details',
    'Classify enquiry urgency and type',
    'Flag hot leads for personal follow-up',
  ],
  'book-jobs': [
    'Extract booking details from enquiry',
    'Find next available time slot',
    'Generate booking confirmation',
  ],
  'follow-up': [
    'Check days since last contact',
    'Determine follow-up priority',
    'Draft personalised follow-up message',
  ],
  'website-enquiries': [
    'Capture form submission or website enquiry',
    'Extract customer details and intent',
    'Route to the right team member or auto-respond',
  ],
  'reactivate': [
    'Identify customers who haven\'t booked recently',
    'Personalise win-back message based on history',
    'Schedule re-engagement follow-up',
  ],
  'stay-organised': [
    'Sync new messages and job updates',
    'Flag overdue tasks and missed replies',
    'Compile daily summary of open items',
  ],
};

/* ─────────────────────────────────────────────── */
/*  OUTCOME → DEFAULT TRIGGER MAPPING               */
/* ─────────────────────────────────────────────── */

const OUTCOME_DEFAULT_TRIGGERS = {
  'never-miss-lead': 'When a new customer enquiry arrives',
  'book-jobs': 'When a booking request is received',
  'follow-up': 'When a follow-up is due',
  'website-enquiries': 'When a website form is submitted',
  'reactivate': 'When a customer hasn\'t booked in a while',
  'stay-organised': 'When new messages or updates come in',
};

/* ─────────────────────────────────────────────── */
/*  APP → PROVIDER/CAPABILITY MAPPING               */
/*  Mirrors backend capability_registry.py           */
/* ─────────────────────────────────────────────── */

const APP_PROVIDER_MAP = {
  'Gmail':            { provider: 'gmail',           triggerCap: 'receive_email',   actionCap: 'send_email' },
  'Outlook':          { provider: 'outlook',         triggerCap: 'receive_email',   actionCap: 'send_email' },
  'Google Calendar':  { provider: 'google_calendar', triggerCap: null,              actionCap: 'create_event' },
  'Calendly':         { provider: 'google_calendar', triggerCap: null,              actionCap: 'create_event' },
};

let _stepCounter = 0;
function nextStepId() {
  _stepCounter += 1;
  return `step_${Date.now().toString(36)}_${_stepCounter}`;
}

/**
 * Build a structured step with all execution fields.
 */
function buildStep({ type, label, app, providerOverride, capabilityOverride }) {
  const mapping = app ? APP_PROVIDER_MAP[app] : null;
  const provider = providerOverride || (mapping ? mapping.provider : (type === 'ai' ? 'ai' : ''));
  let capability = capabilityOverride || '';

  if (!capability && mapping) {
    if (type === 'trigger') capability = mapping.triggerCap || '';
    else if (type === 'action') capability = mapping.actionCap || '';
  }
  if (!capability && type === 'ai') {
    const lower = (label || '').toLowerCase();
    if (lower.includes('reply') || lower.includes('respond')) capability = 'generate_reply';
    else if (lower.includes('summary')) capability = 'generate_summary';
    else if (lower.includes('classif') || lower.includes('urgency')) capability = 'classify_enquiry';
    else if (lower.includes('quote')) capability = 'generate_quote';
    else capability = 'generate_reply';
  }
  if (!capability && (type === 'logic' || type === 'condition')) {
    const lower = (label || '').toLowerCase();
    if (lower.includes('hour') || lower.includes('time')) {
      capability = 'check_business_hours';
    }
  }

  return {
    id: nextStepId(),
    type: type || 'action',
    label: label || 'Step',
    app: app || null,
    provider,
    capability,
    execution_mode: 'auto',
  };
}

/* ─────────────────────────────────────────────── */
/*  MAIN GENERATOR                                  */
/* ─────────────────────────────────────────────── */

/**
 * Generate a dynamic workflow based on outcome + selected apps.
 *
 * Rules:
 * - Every selected app MUST appear in the workflow
 * - Minimum: 1 trigger, 2 process steps, 1 output
 * - Outcome determines the "base structure" (context-specific process steps)
 * - Apps determine the specific tool references
 *
 * @param {string} outcomeId - The selected outcome ID
 * @param {Set|Array} selectedApps - Set or array of selected app names
 * @returns {Array<{type: string, label: string, app: string|null}>}
 */
export function generateWorkflow(outcomeId, selectedApps) {
  const apps = Array.from(selectedApps);
  const steps = [];
  const usedApps = new Set();

  // 1. Determine trigger
  const triggerApp = apps.find(a => APP_ROLES[a]?.capabilities.includes('trigger'));
  if (triggerApp) {
    steps.push(buildStep({
      type: 'trigger',
      label: APP_ROLES[triggerApp].trigger,
      app: triggerApp,
    }));
    usedApps.add(triggerApp);
  } else {
    steps.push(buildStep({
      type: 'trigger',
      label: OUTCOME_DEFAULT_TRIGGERS[outcomeId] || 'When a new event occurs',
      app: null,
    }));
  }

  // 2. Add outcome-specific processing steps (AI intelligence)
  const processSteps = OUTCOME_PROCESS_STEPS[outcomeId] || [
    'Extract key details from input',
    'Process and validate information',
  ];
  // Add first core process step (always present)
  steps.push(buildStep({
    type: 'ai',
    label: processSteps[0],
    app: null,
    providerOverride: 'ai',
  }));

  // 3. Map remaining apps to process/output roles
  const remainingApps = apps.filter(a => !usedApps.has(a));

  // Separate into process-capable and output-only apps
  const processApps = remainingApps.filter(
    a => APP_ROLES[a]?.capabilities.includes('process')
  );
  const outputApps = remainingApps.filter(
    a => APP_ROLES[a]?.capabilities.includes('output') && !APP_ROLES[a]?.capabilities.includes('process')
  );
  // Apps that can do both — prefer process role first, then output
  const dualApps = remainingApps.filter(
    a => APP_ROLES[a]?.capabilities.includes('process') && APP_ROLES[a]?.capabilities.includes('output')
  );

  // Add process steps for apps
  for (const app of processApps) {
    if (usedApps.has(app)) continue;
    const role = APP_ROLES[app];
    if (role?.process) {
      steps.push(buildStep({ type: 'action', label: role.process, app }));
      usedApps.add(app);
    }
  }

  // Add more outcome-specific steps if we need more processing depth
  if (steps.length < 4 && processSteps.length > 1) {
    steps.push(buildStep({
      type: 'ai',
      label: processSteps[1],
      app: null,
      providerOverride: 'ai',
    }));
  }

  // 4. Add output steps
  // First, use the trigger app as output too if it has output capability (e.g., Gmail send reply)
  if (triggerApp && APP_ROLES[triggerApp]?.capabilities.includes('output') && APP_ROLES[triggerApp]?.output) {
    steps.push(buildStep({
      type: 'action',
      label: APP_ROLES[triggerApp].output,
      app: triggerApp,
    }));
  }

  // Add output-only apps
  for (const app of outputApps) {
    if (usedApps.has(app)) continue;
    const role = APP_ROLES[app];
    if (role?.output) {
      steps.push(buildStep({ type: 'action', label: role.output, app }));
      usedApps.add(app);
    }
  }

  // Add dual apps that weren't used as process steps → use as output
  for (const app of dualApps) {
    if (usedApps.has(app)) continue;
    const role = APP_ROLES[app];
    if (role?.output) {
      steps.push(buildStep({ type: 'action', label: role.output, app }));
      usedApps.add(app);
    }
  }

  // 5. Catch any apps not yet in workflow
  for (const app of apps) {
    if (usedApps.has(app)) continue;
    const role = APP_ROLES[app];
    if (!role) {
      // Unknown app — add a generic process step
      steps.push(buildStep({ type: 'action', label: `Process via ${app}`, app }));
      usedApps.add(app);
      continue;
    }
    // Try process, then output, then trigger as fallback
    if (role.process) {
      steps.push(buildStep({ type: 'action', label: role.process, app }));
    } else if (role.output) {
      steps.push(buildStep({ type: 'action', label: role.output, app }));
    } else if (role.trigger) {
      steps.push(buildStep({ type: 'action', label: role.trigger.replace('New ', 'Monitor '), app }));
    }
    usedApps.add(app);
  }

  // 6. Ensure minimum quality: at least 4 steps total (1 trigger + 2 process + 1 output)
  if (steps.length < 4) {
    // Add remaining outcome-specific steps
    for (let i = 1; i < processSteps.length && steps.length < 4; i++) {
      const existing = steps.find(s => s.label === processSteps[i]);
      if (!existing) {
        // Insert before the last step (which is likely an output)
        steps.splice(steps.length - 1, 0, buildStep({
          type: 'ai',
          label: processSteps[i],
          app: null,
          providerOverride: 'ai',
        }));
      }
    }
  }

  // Ensure we always end with at least one output
  const hasOutput = steps.length > 1; // trigger + at least one action
  if (!hasOutput) {
    steps.push(buildStep({
      type: 'action',
      label: 'Deliver processed result',
      app: null,
    }));
  }

  return steps;
}

/**
 * Get the role definition for an app.
 */
export function getAppRole(appName) {
  return APP_ROLES[appName] || null;
}

/**
 * Check if an app can be meaningfully used in a workflow.
 * Returns false for apps we have no mapping for.
 */
export function isAppUsable(appName) {
  return !!APP_ROLES[appName];
}

/**
 * Get a display-friendly summary of what the workflow does.
 */
export function getWorkflowSummary(steps) {
  if (!steps || steps.length === 0) return '';
  const trigger = steps.find(s => s.type === 'trigger');
  const actions = steps.filter(s => s.type === 'action');
  const parts = [];
  if (trigger) parts.push(trigger.label);
  if (actions.length > 0) parts.push(...actions.map(a => a.label.toLowerCase()));
  return parts.join(', then ');
}

/**
 * Generate a workflow directly from a marketplace template.
 * Uses template.how_it_works_steps + template.works_with to build structured steps.
 *
 * @param {Object} template - The marketplace template object
 * @returns {Array<{type: string, label: string, app: string|null}>}
 */
export function generateWorkflowFromTemplate(template) {
  const apps = template.works_with || [];
  const howSteps = template.how_it_works_steps || [];
  const steps = [];
  const usedApps = new Set();

  // 1. Determine trigger from template data
  const triggerType = template.trigger_type || '';
  const triggerApp = apps.find(a => APP_ROLES[a]?.capabilities.includes('trigger'));

  if (triggerApp && APP_ROLES[triggerApp]?.trigger) {
    steps.push({ type: 'trigger', label: APP_ROLES[triggerApp].trigger, app: triggerApp });
    usedApps.add(triggerApp);
  } else if (howSteps.length > 0) {
    steps.push({ type: 'trigger', label: howSteps[0], app: null });
  } else {
    steps.push({ type: 'trigger', label: 'When triggered', app: null });
  }

  // 2. Convert how_it_works_steps into action steps, matching with apps
  const actionSteps = howSteps.slice(triggerApp ? 0 : 1); // Skip first if used as trigger
  for (const desc of actionSteps) {
    // Try to match this step description with an unused app
    const matchedApp = apps.find(a => {
      if (usedApps.has(a)) return false;
      const lower = desc.toLowerCase();
      const appLower = a.toLowerCase();
      return lower.includes(appLower) ||
        (a === 'Gmail' && (lower.includes('email') || lower.includes('mail'))) ||
        (a === 'Google Calendar' && (lower.includes('calendar') || lower.includes('appointment') || lower.includes('schedule'))) ||
        (a === 'Calendly' && (lower.includes('calendly') || lower.includes('booking') || lower.includes('schedule'))) ||
        (a === 'Slack' && lower.includes('slack')) ||
        (a === 'Google Sheets' && (lower.includes('sheet') || lower.includes('spreadsheet') || lower.includes('log') || lower.includes('save'))) ||
        (a === 'HubSpot' && (lower.includes('hubspot') || lower.includes('crm') || lower.includes('contact'))) ||
        (a === 'Xero' && (lower.includes('xero') || lower.includes('invoice'))) ||
        (a === 'QuickBooks' && (lower.includes('quickbooks') || lower.includes('invoice'))) ||
        (a === 'Stripe' && (lower.includes('stripe') || lower.includes('payment')));
    });

    steps.push({
      type: 'action',
      label: desc,
      app: matchedApp || null,
    });
    if (matchedApp) usedApps.add(matchedApp);
  }

  // 3. Ensure every app in works_with appears in the workflow
  for (const app of apps) {
    if (usedApps.has(app)) continue;
    const role = APP_ROLES[app];
    if (!role) {
      steps.push({ type: 'action', label: `Process via ${app}`, app });
    } else if (role.process) {
      steps.push({ type: 'action', label: role.process, app });
    } else if (role.output) {
      steps.push({ type: 'action', label: role.output, app });
    }
    usedApps.add(app);
  }

  // 4. Ensure minimum quality: at least 4 steps
  if (steps.length < 4) {
    steps.splice(1, 0, { type: 'action', label: 'Process and validate information', app: null });
  }

  return steps;
}

/**
 * Map selected tools to their functional roles for the confirmation UI.
 * Returns an array of { role: string, tool: string, capability: string }
 *
 * @param {Set|Array} selectedApps
 * @returns {Array<{role: string, tool: string, capability: string}>}
 */
export function getToolRoleMapping(selectedApps) {
  const apps = Array.from(selectedApps);
  const mapping = [];
  const usedCapabilities = new Set();

  const CAPABILITY_LABELS = {
    communication: 'Email',
    scheduling: 'Scheduling',
    data: 'Data storage',
    notification: 'Notifications',
    crm: 'CRM',
    accounting: 'Accounting',
    payment: 'Payments',
    marketing: 'Marketing',
    job_management: 'Job management',
    reviews: 'Reviews',
    social: 'Social media',
    meetings: 'Meetings',
    automation: 'Automation',
  };

  for (const app of apps) {
    const role = APP_ROLES[app];
    if (!role) {
      mapping.push({ role: 'Custom tool', tool: app, capability: 'other' });
      continue;
    }
    const cap = role.category;
    if (!usedCapabilities.has(cap)) {
      usedCapabilities.add(cap);
      mapping.push({
        role: CAPABILITY_LABELS[cap] || cap,
        tool: app,
        capability: cap,
      });
    } else {
      mapping.push({
        role: CAPABILITY_LABELS[cap] || cap,
        tool: app,
        capability: cap,
      });
    }
  }

  return mapping;
}

/**
 * Check if a workflow has tools that are not connected (for draft/limited labeling).
 * @param {Array} steps - workflow steps
 * @param {Set} connectedApps - set of connected app slugs
 * @returns {{ status: 'ready'|'limited'|'draft', unconnectedTools: string[] }}
 */
export function getWorkflowToolStatus(steps, connectedApps) {
  if (!steps || steps.length === 0) return { status: 'draft', unconnectedTools: [] };
  const toolSteps = steps.filter(s => s.app);
  if (toolSteps.length === 0) return { status: 'ready', unconnectedTools: [] };

  const connected = connectedApps instanceof Set ? connectedApps : new Set(connectedApps || []);
  const unconnected = [];

  for (const step of toolSteps) {
    const slug = step.app.toLowerCase().replace(/\s+/g, '');
    const slugDash = step.app.toLowerCase().replace(/\s+/g, '-');
    if (!connected.has(slug) && !connected.has(slugDash) && !connected.has(step.app.toLowerCase())) {
      unconnected.push(step.app);
    }
  }

  if (unconnected.length === toolSteps.length) return { status: 'draft', unconnectedTools: [...new Set(unconnected)] };
  if (unconnected.length > 0) return { status: 'limited', unconnectedTools: [...new Set(unconnected)] };
  return { status: 'ready', unconnectedTools: [] };
}

export default { generateWorkflow, generateWorkflowFromTemplate, getAppRole, isAppUsable, getWorkflowSummary, getToolRoleMapping, getWorkflowToolStatus };
